import { BigNumber } from '@ethersproject/bignumber';
import { formatUnits, parseUnits } from '@ethersproject/units';

import { IDexSLSimulationOptions } from 'types/bots';
import { durationToMs } from 'utils/duration';

export type DelayUnit = 'seconds' | 'minutes';

export const isRandomStrategyConfigured = (
  config: IDexSLSimulationOptions | undefined,
): config is IDexSLSimulationOptions => {
  const strategy = config?.config?.random_strategy;
  if (!strategy) return false;
  if (!(Number(strategy.min_price_percentage) > 0)) return false;
  if (!(Number(strategy.max_price_percentage) > 0)) return false;
  if (!strategy.target_factory?.blocks?.length) return false;

  try {
    const minPause = durationToMs(strategy.min_pause_between_trades);
    const maxPause = durationToMs(strategy.max_pause_between_trades);
    return minPause > 0 && maxPause >= minPause;
  } catch {
    return false;
  }
};

const trimFraction = (value: string) => {
  const [integer, fraction] = value.split('.');
  if (fraction === undefined) return integer;
  const trimmedFraction = fraction.replace(/0+$/, '');
  return trimmedFraction ? `${integer}.${trimmedFraction}` : integer;
};

export const parseTokenAmount = (value: string, decimals: number) =>
  parseUnits(value.trim(), decimals).toString();

export const formatTokenAmount = (value: string, decimals: number) =>
  trimFraction(formatUnits(BigNumber.from(value), decimals));

export const getAmountRangeError = (
  minValue: string,
  maxValue: string,
  decimals: number,
): string | undefined => {
  if (!minValue.trim() || !maxValue.trim()) return 'Enter minimum and maximum size';

  try {
    const min = BigNumber.from(parseTokenAmount(minValue, decimals));
    const max = BigNumber.from(parseTokenAmount(maxValue, decimals));
    if (min.lte(0) || max.lte(0)) return 'Size must be greater than zero';
    if (max.lt(min)) return 'Maximum size must be at least minimum size';
    return undefined;
  } catch {
    return 'Enter a valid size';
  }
};

export const hasCompleteAmountRange = (minValue: string, maxValue: string) =>
  !!minValue.trim() && !!maxValue.trim();

interface IQuoteEquivalentParams {
  baseAmount: string;
  baseDecimals: number;
  quoteDecimals: number;
  quotePriceRaw: string;
}

export const getQuoteEquivalent = ({
  baseAmount,
  baseDecimals,
  quoteDecimals,
  quotePriceRaw,
}: IQuoteEquivalentParams) => {
  if (!baseAmount.trim() || !quotePriceRaw) return undefined;

  try {
    const baseRaw = BigNumber.from(parseTokenAmount(baseAmount, baseDecimals));
    const quoteRaw = baseRaw
      .mul(BigNumber.from(quotePriceRaw))
      .div(BigNumber.from(10).pow(baseDecimals));
    return formatTokenAmount(quoteRaw.toString(), quoteDecimals);
  } catch {
    return undefined;
  }
};

export const formatDelay = (value: string, unit: DelayUnit) =>
  `${value}${unit === 'minutes' ? 'm' : 's'}`;

export const normalizeSimulationVolume = (rawVolume: number, decimals: number) => {
  const volume = rawVolume / 10 ** decimals;
  return Number.isFinite(volume) ? volume : 0;
};

interface IMigrateSimulationParams {
  actualSeed: string;
  minAmount: string;
  maxAmount: string;
}

export const migrateSimulationConfig = (
  source: IDexSLSimulationOptions,
  { actualSeed, minAmount, maxAmount }: IMigrateSimulationParams,
) => {
  const resolvedConfig: IDexSLSimulationOptions = {
    ...source,
    seed: source.seed < 0 ? Number(actualSeed) : source.seed,
  };
  const strategy = resolvedConfig.config.random_strategy;
  const min = BigNumber.from(minAmount);
  const max = BigNumber.from(maxAmount);
  const migratedAmounts =
    !strategy.min_transaction_amount_base &&
    !strategy.max_transaction_amount_base &&
    min.gt(0) &&
    max.gte(min);

  if (!migratedAmounts) {
    return { config: resolvedConfig, simulatedConfig: resolvedConfig, migratedAmounts };
  }

  return {
    migratedAmounts,
    simulatedConfig: resolvedConfig,
    config: {
      ...resolvedConfig,
      config: {
        ...resolvedConfig.config,
        random_strategy: {
          ...strategy,
          min_transaction_amount_base: minAmount,
          max_transaction_amount_base: maxAmount,
        },
      },
    },
  };
};
